import { tabKeyTarget } from './lf-utils.js';

/**
 * Product tabs, following the WAI-ARIA tabs pattern with automatic activation. Markup contract: tabs are
 * `[role="tab"]` buttons inside a `[role="tablist"]`, each with `aria-controls` naming its `[role="tabpanel"]`. Every
 * panel is rendered by the server and the inactive ones carry `hidden`, so switching never waits on the network.
 *
 * Only the selected tab is in the tab order. Arrow Left and Right wrap around the tabs (mirrored in right-to-left
 * pages), and Home and End jump to the first and last tab. Once the page has loaded and is idle, the lazy images of the
 * hidden panels are switched to low-priority eager loads, so the first switch to a tab shows its cards at once.
 */
class LfTabs extends HTMLElement {
  /** @type {HTMLElement[]} */
  #tabs = [];
  #idleId = 0;
  #timerId = 0;

  connectedCallback() {
    this.#tabs = /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tablist"] [role="tab"]')]);
    if (this.#tabs.length === 0) return;
    this.#select(Math.max(0, this.#tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true')));

    this.addEventListener('click', this.#onClick);
    this.addEventListener('keydown', this.#onKeydown);

    // Browsers never fetch lazy images inside display:none, so the hidden panels are warmed once the page is idle.
    if (document.readyState === 'complete') this.#schedulePreload();
    else window.addEventListener('load', this.#schedulePreload, { once: true });
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('keydown', this.#onKeydown);
    window.removeEventListener('load', this.#schedulePreload);
    if (this.#idleId) cancelIdleCallback(this.#idleId);
    window.clearTimeout(this.#timerId);
    this.#idleId = 0;
    this.#timerId = 0;
  }

  /** Runs after the window load event, at idle, so the extra requests never compete with the LCP image. */
  #schedulePreload = () => {
    if (this.#idleId || this.#timerId) return;
    const run = () => {
      this.#idleId = 0;
      this.#timerId = 0;
      this.#preloadHiddenImages();
    };
    if (typeof requestIdleCallback === 'function') this.#idleId = requestIdleCallback(run, { timeout: 4000 });
    else this.#timerId = window.setTimeout(run, 2000);
  };

  #preloadHiddenImages() {
    for (const image of this.querySelectorAll('[role="tabpanel"][hidden] img[loading="lazy"]')) {
      if (!(image instanceof HTMLImageElement)) continue;
      image.fetchPriority = 'low';
      image.loading = 'eager';
    }
  }

  /**
   * @param {number} index
   * @param {boolean} [focus] - Move keyboard focus to the newly selected tab.
   */
  #select(index, focus = false) {
    this.#tabs.forEach((tab, position) => {
      const active = position === index;
      tab.setAttribute('aria-selected', String(active));
      tab.tabIndex = active ? 0 : -1;
      const panel = this.#panelFor(tab);
      if (panel) panel.hidden = !active;
    });
    if (focus) this.#tabs[index]?.focus();
  }

  /**
   * The panel a tab controls. Normally it is inside this element; the search page's product grid is a separate section
   * further down the page, so a panel found only by id elsewhere in the document is accepted too and is given the
   * tabpanel role and named by its tab here, because the server cannot render it with them.
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    if (!id) return null;
    const inside = this.querySelector(`#${CSS.escape(id)}`);
    const panel = inside ?? document.getElementById(id);
    if (!(panel instanceof HTMLElement)) return null;
    if (!inside) {
      panel.setAttribute('role', 'tabpanel');
      if (tab.id) panel.setAttribute('aria-labelledby', tab.id);
    }
    return panel;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    const position = tab ? this.#tabs.indexOf(/** @type {HTMLElement} */ (tab)) : -1;
    if (position !== -1) this.#select(position);
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    const tab = event.target instanceof Element ? event.target.closest('[role="tab"]') : null;
    const current = tab ? this.#tabs.indexOf(/** @type {HTMLElement} */ (tab)) : -1;
    if (current === -1 || event.altKey || event.ctrlKey || event.metaKey) return;

    const next = tabKeyTarget(event.key, current, this.#tabs.length, getComputedStyle(this).direction === 'rtl');
    if (next === -1) return;
    event.preventDefault();
    this.#select(next, true);
  };
}

if (!customElements.get('lf-tabs')) customElements.define('lf-tabs', LfTabs);
